import { z } from 'zod'
import { AccountType } from '../../../enums/account-type.enum'
import { Currency } from '../../../enums/currency.enum'

// shared fields across all breakdown item variants
const baseItemFields = { isReadOnly: z.boolean() }

const categoryItemSchema = z.object({
  ...baseItemFields,
  id: z.string().guid(),
  date: z.string(),
  amount: z.number(),
  description: z.string(),
  // `category` may be `null` on EXPENSE rows that are shared-expense leftovers
  // where the viewer cannot see the per-split category — the frontend renders
  // these under the "Uncategorized" bucket. Real categorised rows continue to
  // carry the real category uuid and name.
  category: z
    .object({
      id: z.string().guid(),
      name: z.string(),
    })
    .nullable(),
  groupName: z.string().nullable(),
})

const accountRefSchema = z.object({
  id: z.string().guid(),
  name: z.string(),
  type: z.nativeEnum(AccountType),
})

const transferItemSchema = z.object({
  ...baseItemFields,
  id: z.string().guid(),
  date: z.string(),
  amount: z.number(),
  fromAccount: accountRefSchema,
  toAccount: accountRefSchema,
})

const unsettledSharedExpenseItemSchema = z.object({
  isReadOnly: z.literal(true),
  id: z.string().guid(),
  date: z.string(),
  amount: z.number(),
  currency: z.nativeEnum(Currency),
  description: z.string(),
  category: z
    .object({
      id: z.string().guid(),
      name: z.string(),
    })
    .nullable(),
  paidByName: z.string(),
  groupName: z.string(),
  sharePercentage: z.number(),
})

export const breakdownItemSchema = z.discriminatedUnion('kind', [
  // EXPENSE rows carry an extra `isUnscaledSettled` flag set when the row is a
  // settled shared expense whose round's settlement endpoint is on an
  // unselected account. ON mode keeps the full amount on these rows because
  // the (1 − share) refund lives on an account the report doesn't see; the UI
  // marks them so the user can reach for "include settlement accounts".
  categoryItemSchema.extend({
    kind: z.literal('EXPENSE'),
    isUnscaledSettled: z.boolean().default(false),
  }),
  categoryItemSchema.extend({ kind: z.literal('INCOME') }),
  transferItemSchema.extend({ kind: z.literal('TRANSFER_IN') }),
  transferItemSchema.extend({ kind: z.literal('TRANSFER_OUT') }),
  unsettledSharedExpenseItemSchema.extend({ kind: z.literal('UNSETTLED_SHARED_EXPENSE') }),
])

export type BreakdownItemDto = z.infer<typeof breakdownItemSchema>

export const monthBreakdownResponseSchema = z.object({
  items: z.array(breakdownItemSchema),
})

export type MonthBreakdownResponseDto = z.infer<typeof monthBreakdownResponseSchema>
